// Shared date range + preset for Statistics (movies, charts, etc.) and Analysis
// screens (accounting, attendance, engine logs, email campaigns). Persisted in
// localStorage so a full reload re-applies the same preset (e.g. "today"
// resolves to the current calendar day) or the same custom ISO bounds.

import moment from 'moment';
import { resolve_date_range_preset } from '../../components/DateRangePresets/dateRangePresets';
import { pickUserIdFromAuthUser, readPersistedSessionIds } from '../../services/kronosPrefs/sessionIds';
import { emptyUserPrefsV1, mergeUserPrefsPatch, readUserPrefsDoc } from '../../services/kronosPrefs/userPrefs';

function resolvePrefsUserId(getState) {
    const fromStore = pickUserIdFromAuthUser(getState()?.auth?.user);
    if (fromStore) {
        return fromStore;
    }
    return readPersistedSessionIds().userId;
}

/** Write `analysisFilters` snapshot to `kronos.user.<id>` (also invoked from middleware). */
function persistAnalysisFiltersSnapshot(getState) {
    if (typeof window === 'undefined' || !window.localStorage) {
        return;
    }
    try {
        const userId = resolvePrefsUserId(getState);
        if (!userId) {
            return;
        }
        const { date_from, date_to, date_preset } = getState().analysisFilters;
        mergeUserPrefsPatch(userId, {
            analysesStatsDateUi: { date_from, date_to, date_preset },
        });
    } catch (_e) {
        /* ignore */
    }
}

export const SET_ANALYSIS_DATE_RANGE = 'kronos/analysisFilters/SET_DATE_RANGE';

/** Historical flat localStorage key (pre-namespaced prefs); no longer written by the app. */
export const ANALYSES_STATS_DATE_UI_STORAGE_KEY = 'kronos.analysesStats.dateUi';

const DEFAULT_LOOKBACK_DAYS = 30;

function default_analysis_date_range() {
    return {
        date_from: moment().subtract(DEFAULT_LOOKBACK_DAYS, 'days').startOf('day').toISOString(),
        date_to: moment().endOf('day').toISOString(),
    };
}

/** Exported for middleware (cinema default) — returns null when no saved analyses/stats date UI. */
export function readPersistedAnalysisDateUi() {
    if (typeof window === 'undefined' || !window.localStorage) {
        return null;
    }
    try {
        const { userId } = readPersistedSessionIds();
        if (!userId) {
            return null;
        }
        const doc = readUserPrefsDoc(userId) || emptyUserPrefsV1();
        const blob = doc.analysesStatsDateUi;
        return blob && typeof blob === 'object' ? blob : null;
    } catch (_e) {
        return null;
    }
}

function build_initial_analysis_state() {
    const defaults = default_analysis_date_range();
    const p = readPersistedAnalysisDateUi();
    if (!p || typeof p !== 'object')
        return { ...defaults, date_preset: 'last30' };

    const preset = typeof p.date_preset === 'string' ? p.date_preset : 'last30';
    if (preset && preset !== 'custom') {
        const r = resolve_date_range_preset(preset, moment());
        if (r?.start) {
            return {
                date_from: r.start.toISOString(),
                date_to: r.end ? r.end.toISOString() : null,
                date_preset: preset,
            };
        }
    }
    const f = p.date_from != null ? moment(p.date_from) : null;
    const t = Object.prototype.hasOwnProperty.call(p, 'date_to') ? moment(p.date_to) : null;
    return {
        date_from: f && f.isValid() ? f.toISOString() : defaults.date_from,
        date_to: t && t.isValid() ? t.toISOString() : (p.date_to === null ? null : defaults.date_to),
        date_preset: 'custom',
    };
}

const initialState = build_initial_analysis_state();

export default function analysisFilters(state = initialState, action) {
    switch (action.type) {
        case SET_ANALYSIS_DATE_RANGE: {
            const next = { ...state };
            if (Object.prototype.hasOwnProperty.call(action, 'date_from'))
                next.date_from = action.date_from;
            if (Object.prototype.hasOwnProperty.call(action, 'date_to'))
                next.date_to = action.date_to;
            if (Object.prototype.hasOwnProperty.call(action, 'date_preset'))
                next.date_preset = action.date_preset;
            return next;
        }
        default:
            return state;
    }
}

/**
 * Persisted snapshot for reload (preset re-resolved when not `custom`).
 * @param {import('redux').Store} store
 */
export function persistAnalysisFiltersMiddleware({ getState }) {
    return (next) => (action) => {
        const out = next(action);
        if (action.type === SET_ANALYSIS_DATE_RANGE) {
            persistAnalysisFiltersSnapshot(getState);
        }
        return out;
    };
}

/**
 * Explicit calendar bounds (marks preset as `custom`).
 * Accepts moment instances, ISO strings or anything `moment()` understands.
 */
export const setAnalysisDateRange = (date_from, date_to) => (dispatch) => {
    const f = date_from != null ? moment(date_from) : null;
    const t = date_to != null ? moment(date_to) : null;
    dispatch({
        type: SET_ANALYSIS_DATE_RANGE,
        date_from: f && f.isValid() ? f.toISOString() : null,
        date_to: t && t.isValid() ? t.toISOString() : null,
        date_preset: 'custom',
    });
};

/**
 * Shared preset for all stats + analysis screens using this slice.
 * Presets such as `today` / `last7` are re-resolved on load against current time.
 */
export const setAnalysisDatePreset = (preset) => (dispatch) => {
    if (preset === 'custom') {
        dispatch({
            type: SET_ANALYSIS_DATE_RANGE,
            date_preset: 'custom',
        });
        return;
    }
    const r = resolve_date_range_preset(preset, moment());
    if (!r?.start) {
        dispatch({
            type: SET_ANALYSIS_DATE_RANGE,
            date_preset: 'custom',
        });
        return;
    }
    dispatch({
        type: SET_ANALYSIS_DATE_RANGE,
        date_from: r.start.toISOString(),
        date_to: r.end ? r.end.toISOString() : null,
        date_preset: preset,
    });
};
